import React, { useState, useEffect } from 'react';
import patientService from '../../services/patientService';

const PatientList = ({ onSelectPatient, onAddPatient, onEditPatient, refreshKey }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPatients();
  }, [currentPage, filterStatus, refreshKey]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 10,
      };

      if (filterStatus) {
        params.status = filterStatus;
      }

      const response = await patientService.getAllPatients(params);
      setPatients(response.data.patients);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching patients:', error);
      alert('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim().length > 0) {
      try {
        const response = await patientService.searchPatients(value);
        setPatients(response.data);
      } catch (error) {
        console.error('Search error:', error);
      }
    } else {
      fetchPatients();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Completed':
        return 'bg-blue-100 text-blue-800';
      case 'Follow-up Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Search and Filter Bar */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Completed">Completed</option>
          <option value="Follow-up Pending">Follow-up Pending</option>
        </select>

        <button
          onClick={onAddPatient}
          className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium"
        >
          + Add Patient
        </button>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
        <div style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}>
          <div style={{ display: 'table-header-group', backgroundColor: '#f9fafb' }}>
            <div style={{ display: 'table-row' }}>
              <div style={{ display: 'table-cell', width: '28%', padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Name
              </div>
              <div style={{ display: 'table-cell', width: '18%', padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Phone
              </div>
              <div style={{ display: 'table-cell', width: '28%', padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Condition
              </div>
              <div style={{ display: 'table-cell', width: '18%', padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Status
              </div>
              <div style={{ display: 'table-cell', width: '8%', padding: '12px 24px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                View
              </div>
            </div>
          </div>

          <div style={{ display: 'table-row-group' }}>
            {patients.length === 0 ? (
              <div style={{ display: 'table-row' }}>
                <div style={{ display: 'table-cell', padding: '32px 24px', textAlign: 'center', color: '#6b7280' }} colSpan="5">
                  No patients found
                </div>
              </div>
            ) : (
              patients.map((patient) => (
                <div key={patient.id} style={{ display: 'table-row', borderTop: '1px solid #e5e7eb' }} className="hover:bg-gray-50">
                  <div style={{ display: 'table-cell', width: '28%', padding: '16px 24px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                      {patient.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Age: {patient.age || 'N/A'} • {patient.gender || 'N/A'}
                    </div>
                  </div>

                  <div style={{ display: 'table-cell', width: '18%', padding: '16px 24px', fontSize: '14px', color: '#111827' }}>
                    {patient.phone}
                  </div>

                  <div style={{ display: 'table-cell', width: '28%', padding: '16px 24px', fontSize: '14px', color: '#111827' }}>
                    {patient.condition || 'N/A'}
                  </div>

                  <div style={{ display: 'table-cell', width: '18%', padding: '16px 24px' }}>
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                        patient.status
                      )}`}
                    >
                      {patient.status}
                    </span>
                  </div>

                  <div style={{ display: 'table-cell', width: '8%', padding: '16px 24px', textAlign: 'center' }}>
                    <button
                      onClick={() => onSelectPatient(patient)}
                      className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {patients.length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-8 text-center text-gray-500">
            No patients found
          </div>
        ) : (
          patients.map((patient) => (
            <div key={patient.id} className="bg-white shadow-md rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="text-base font-medium text-gray-900 mb-1">
                    {patient.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    Age: {patient.age || 'N/A'} • {patient.gender || 'N/A'}
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                    patient.status
                  )}`}
                >
                  {patient.status}
                </span>
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex items-center text-sm">
                  <span className="text-gray-500 w-24">Phone:</span>
                  <span className="text-gray-900">{patient.phone}</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="text-gray-500 w-24">Condition:</span>
                  <span className="text-gray-900">{patient.condition || 'N/A'}</span>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200">
                <button
                  onClick={() => onSelectPatient(patient)}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium text-sm"
                >
                  View Details
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            Previous
          </button>

          <span className="px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PatientList;